import { IAppliedTagsInfo } from '../../../pages/mylibrary/components/addTagsSnackbar';
import { ILibraryMainStore } from '../../../pages/mylibrary/typings/stores';
import LookupApiService from '../../api/lookupApiService';
import { IApplyTagsRequest } from '../../api/models/tags';
import { ISaveUserSettingRequest } from '../../api/models/user';
import TagsApiService from '../../api/tagsApiService';
import UserApiService from '../../api/userApiService';
import { ISafeExecuteAsyncAction } from '../../typings/stores';
import { TagEditorMode } from '../../typings/tags/tagEditorMode';
import { TagsSortOrder } from '../../typings/tags/tagsSortOrder';
import TagsFetcher from '../../utils/tagsFetcher';
import TagUtils from '../../utils/tagUtils';
import AddTags from '../tags/addTags';
import AddTagsInput from '../tags/addTagsInput';
import ApplyButton from '../tags/applyButton';
import useAvailableTags from '../tags/hooks/useAvailableTags';
import PendingTagsInfo from '../tags/pendingTagsInfo';
import ModalWindow from './modalWindow';
import { Box, Stack } from '@mui/material';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';

export interface IAddTagsDialogProps {
	libraryId?: number;
	documentIds: number[];
	safeExecuteAsync?: ISafeExecuteAsyncAction;
	onHide?: () => void;
	onApplyWithInfo?: (appliedTagsInfo: IAppliedTagsInfo) => void;
	lookupApi?: LookupApiService;
	tagsApiService?: TagsApiService;
	userApiService?: UserApiService;
	tagsSort?: TagsSortOrder;
	setSort?: (order: TagsSortOrder) => void;
}

export const AddTagsDialog: React.FunctionComponent<IAddTagsDialogProps> = ({
	libraryId,
	documentIds,
	tagsApiService,
	safeExecuteAsync,
	lookupApi,
	onHide,
	onApplyWithInfo,
	userApiService,
	tagsSort,
	setSort
}) => {
	const [appliedTags, setAppliedTags] = useState<string[]>([]);
	const [inputValue, setInputValue] = useState('');
	const [applying, setApplying] = useState(false);

	const searchTimeout = useRef<NodeJS.Timeout>(null);
	const timeoutMs = 1000;
	const filterRef = useRef('');
	const compareTags = TagUtils.equals;
	const isChanged = useRef(false);

	const inputRef = React.useRef<HTMLInputElement>(null);

	const updateFetcher = (callback?: () => boolean) => {
		if (searchTimeout.current) {
			clearTimeout(searchTimeout.current);
		}

		searchTimeout.current = setTimeout(() => {
			if (callback && !callback()) {
				return;
			}

			setFetcher(createFetcher());
		}, timeoutMs);
	};

	const createFetcher = () => {
		return new TagsFetcher({
			api: lookupApi,
			libraryId: libraryId,
			sort: tagsSort,
			filter: filterRef.current,
			hideZeroCount: true
		});
	};
	const initFetcher = useRef(createFetcher());

	const [availableTagsControl, , , , setSelectedTags, , setFetcher] = useAvailableTags({
		fetcher: initFetcher.current,
		onSelect: (tag: string) => {
			isChanged.current = true;
			if (!appliedTags.some(t => compareTags(t, tag))) {
				setAppliedTags([...appliedTags, tag]);
			}
		},
		safeExecuteAsync: safeExecuteAsync,
		tagsEqual: compareTags,
		tagIncludes: TagUtils.includes,
		tagEditorMode: TagEditorMode.NoAdding,
		sortOrder: tagsSort,
		onSortOrderChange: async newSortOrder => {
			setSort(newSortOrder);

			const request = {
				settingKey: 'MyLibraryAddTagsDlg',
				updatedSettings: { TagsSortBy: newSortOrder }
			} as ISaveUserSettingRequest;

			await safeExecuteAsync(async () => await userApiService.saveUserSetting(request), null);
		}
	});

	useEffect(() => setSelectedTags([...appliedTags]), [appliedTags]);
	useEffect(() => setFetcher(createFetcher()), [tagsSort]);

	React.useEffect(() => {
		inputRef.current?.focus();
	}, [inputRef.current]);

	const handleApply = async () => {
		setApplying(true);

		const request: IApplyTagsRequest = {
			documentIds: documentIds,
			libraryId: libraryId,
			tags: appliedTags
		};

		const result = await safeExecuteAsync(async () => await tagsApiService.addTagsWithInfoAsync(request), null);

		setApplying(false);

		if (result && onApplyWithInfo) {
			const newAppliedTagsInfo: IAppliedTagsInfo = {
				addedTagsCount: result.addedTagsCount,
				notAddedTagsCount: result.notAddedTagsCount
			};
			onApplyWithInfo(newAppliedTagsInfo);
		}

		if (onHide) {
			onHide();
		}
	};

	const onInputChange = (input: string) => {
		setInputValue(input);

		if (searchTimeout.current) {
			clearTimeout(searchTimeout.current);
		}

		updateFetcher(() => {
			if (compareTags(input, filterRef.current)) {
				return false;
			}

			filterRef.current = input;
			return true;
		});
	};

	const actions = [
		<Stack key="actions" direction="row" justifyContent="space-between" alignItems="center">
			<Box sx={{ position: 'absolute', left: '1rem' }}>
				<PendingTagsInfo assignedTagsCount={appliedTags.length} />
			</Box>
			<Box>
				<button disabled={applying} className="rup-btn rup-btn--lg" type="button" onClick={onHide}>
					{localize('Cancel')}
				</button>
				<ApplyButton
					applying={applying}
					disabled={!isChanged.current || appliedTags.length === 0}
					onClick={!applying && isChanged.current ? handleApply : null}
					changesCount={appliedTags.length}
					large
				/>
			</Box>
		</Stack>
	];

	const content = (
		<Stack direction="column" spacing={4}>
			<AddTagsInput inputValue={inputValue} onInput={onInputChange} searchOnly ref={inputRef} />
			<Stack direction="row" spacing={6} style={{ maxHeight: '19rem' }}>
				{availableTagsControl}
				<AddTags
					title={localize('Tags currently assigned to items')}
					tags={appliedTags}
					onRemove={tag => {
						isChanged.current = true;
						setAppliedTags(appliedTags.filter(t => !TagUtils.equals(t, tag)));
					}}
					width="22.75rem"
				/>
			</Stack>
		</Stack>
	);

	return (
		<ModalWindow
			title={localize('Assign Tags')}
			width="60rem"
			maxWidth="60rem"
			content={content}
			actions={actions}
			closeHandler={onHide}
			disableAutoClose={true}
		></ModalWindow>
	);
};

export default inject((stores: ILibraryMainStore) => ({
	libraryId: stores.viewModel.libraryId,
	lookupApi: stores.api.lookupApiService,
	safeExecuteAsync: stores.safeExecuteAsync,
	tagsApiService: stores.api.tagsApiService,
	userApiService: stores.api.userApiService,
	tagsSort: stores.userOptionsStore.addTagsDlgSort,
	setSort: stores.userOptionsStore.setAddTagsDlgSort
}))(observer(AddTagsDialog));
